import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconDeviceList, IconEye, IconGridView, IconListView } from '../../../../../assets/popup-nav-icons';
import { TabType } from '../../../../../components/tabs';
import { useInstallationContext } from '../../../../../context/installation';
import { UserPermissionType } from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../routes/routes';
import { isLocalApp } from '../../../../../utils/helpers/local-app';
import { useDevicesAndChannels } from '../../../hooks/devices-and-channels';
import * as storage from '../../../storage';

export const useChannelList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('channel-list');
  const { t: tc } = useTranslation('common');
  const { t: tdash } = useTranslation('dashboard');
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [isListView, setIsListView] = useState<boolean>(!!storage.getItem('isChannelsListView'));
  const { channelGroups } = useDevicesAndChannels();
  const { selectedInstallation } = useInstallationContext();
  const { permissions } = useUserPermissions();

  useEffect(() => {
    const channelsTabIndex = storage.getItem('lastActiveTabInChannelList') || 0;
    if (channelsTabIndex < channelGroups.length) {
      setActiveTabIndex(channelsTabIndex);
    }
  }, [channelGroups.length]);

  const handleSetActiveTabIndex = (index: number) => {
    setActiveTabIndex(index);
    storage.setItem('lastActiveTabInChannelList', index);
  };

  const toggleListView = () => {
    storage.setItem('isChannelsListView', !isListView);
    setIsListView(!isListView);
  };

  const tabs: TabType[] = useMemo(
    () => channelGroups.map((channelGroups) => ({ label: tc(`typesPlural.${channelGroups.type}`) })),
    [channelGroups],
  );

  const popUpNavLinks = useMemo(
    () => [
      ...(permissions[UserPermissionType.DeviceList]
        ? [
            {
              onClick: () => navigate(ROUTES.Device()),
              label: t('devicesList'),
              icon: <IconDeviceList />,
            },
          ]
        : []),
      ...(!isLocalApp
        ? [
            {
              onClick: () => navigate(ROUTES.ChannelsVisibility()),
              label: t('editVisibility'),
              hasLineBelow: true,
              icon: <IconEye />,
            },
          ]
        : []),
      {
        onClick: toggleListView,
        label: isListView ? tdash('gridView') : tdash('listView'),
        icon: isListView ? <IconGridView /> : <IconListView />,
      },
    ],
    [selectedInstallation?.accessType, isListView, permissions],
  );

  return {
    isListView,
    popUpNavLinks,
    tabs,
    activeTabIndex,
    channelGroups,
    handleSetActiveTabIndex,
  };
};
